import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { FileSpreadsheet, Download, AlertCircle, CheckCircle2, ArrowLeft, Timer, ShieldCheck, Info, HelpCircle, FileArchive, Send, Mail } from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoDirfEsocial = ({ embedded = false }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Estados de Ajuda
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Variável para a saudação do cabeçalho
    const nomeUsuario = "Júlia"; 

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => { return window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'); };

    // ==========================================
    // ESTADOS GERAIS
    // ==========================================
    const [mensagem, setMensagem] = useState(null);
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando ficheiros...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    // ==========================================
    // ESTADOS: ANÁLISE DIRF
    // ==========================================
    const [fileTotal, setFileTotal] = useState(null);
    const [fileTit, setFileTit] = useState(null);
    const [fileDep, setFileDep] = useState(null);
    const [carregandoAnalise, setCarregandoAnalise] = useState(false);

    // ==========================================
    // ESTADOS: DISPARO DE E-MAILS
    // ==========================================
    const [fileZip, setFileZip] = useState(null);
    const [carregandoDisparo, setCarregandoDisparo] = useState(false);

    // Polling da Barra de Progresso (Compartilhado)
    useEffect(() => {
        let interval;
        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const res = await fetch(`/api/automacao-rh/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                    });
                    const data = await res.json();
                    
                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = lastLog.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '');
                        setStatusText(cleanLog);

                        // Lógica de progresso baseada nas palavras-chave do backend
                        if (cleanLog.includes('Iniciando')) setProgress(10);
                        else if (cleanLog.includes('Resumo') || cleanLog.includes('Descompactando')) setProgress(30);
                        else if (cleanLog.includes('cruzar dados') || cleanLog.includes('Encontrados')) setProgress(50);
                        else if (cleanLog.includes('mapeamento') || cleanLog.includes('Buscando')) setProgress(75);
                        else if (cleanLog.includes('gerar ficheiro') || cleanLog.includes('Disparos concluídos')) setProgress(90);
                        else if (cleanLog.includes('Processo totalmente finalizado')) {
                            // GATILHO DE SUCESSO DO DISPARO
                            setProgress(100);
                            setIsPolling(false);
                            setCarregandoDisparo(false);
                            setMensagem({ type: 'success', text: 'Disparos concluídos! Baixando relatório de controle...' });
                            
                            // Dispara a requisição para a nova rota GET baixar o Excel
                            fetch(`/api/automacao-rh/download-dirf-disparo/${executionId}`, {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                            })
                            .then(async (res) => {
                                // NOVA TRAVA DE SEGURANÇA: Se o status não for 2xx, extrai o erro e barra o download
                                if (!res.ok) {
                                    const errData = await res.json();
                                    throw new Error(errData.error || 'Erro ao baixar o relatório no servidor.');
                                }
                                return res.blob();
                            })
                            .then(blob => {
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'Controle_Disparo_DIRF.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                link.parentNode.removeChild(link);
                                setFileZip(null);
                                document.getElementById('form-dirf-disparo').reset();
                            })
                            .catch(error => {
                                // Mostra na tela o motivo real do erro (Ex: "Arquivo não encontrado")
                                setMensagem({ type: 'error', text: error.message });
                            });
                        }
                        else if (cleanLog.includes('Sucesso') && !cleanLog.includes('Status do Envio')) {
                            // Sucesso da Análise DIRF (Mantém a lógica antiga)
                            setProgress(100);
                        }
                        else if (cleanLog.includes('[ERRO FATAL]')) {
                            setIsPolling(false);
                            setCarregandoDisparo(false);
                            setMensagem({ type: 'error', text: cleanLog });
                        }
                    }
                } catch(e) {}
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPolling, executionId]);


    // ==========================================
    // SUBMIT: DISPARO DE E-MAILS (AJUSTADO PARA BACKGROUND)
    // ==========================================
    const handleDisparoSubmit = async (e) => {
        e.preventDefault();
        setCarregandoDisparo(true); setMensagem(null);
        setProgress(5); setStatusText('Enviando arquivo ZIP para o servidor...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); 

        const formData = new FormData();
        formData.append('executionId', newId);
        formData.append('arquivo_zip', fileZip);

        try {
            const response = await fetch('/api/automacao-rh/rh-dirf-disparo', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            if (response.ok) {
                // A requisição terminou rápido, mas o backend vai trabalhar! 
                // Então ativamos o polling para acompanhar o progresso.
                setIsPolling(true); 
                setStatusText('Servidor processando os envios em segundo plano...');
            } else {
                const data = await response.json();
                setMensagem({ type: 'error', text: data.error || 'Erro ao realizar o disparo.' });
                setCarregandoDisparo(false);
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de ligação ao servidor durante o upload.' });
            setCarregandoDisparo(false);
        }
    };

    // ==========================================
    // SUBMIT: ANÁLISE DIRF
    // ==========================================
    const handleAnaliseSubmit = async (e) => {
        e.preventDefault();
        setCarregandoAnalise(true); setMensagem(null);
        setProgress(5); setStatusText('A preparar o processamento...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newId);
        formData.append('file_total', fileTotal);
        formData.append('file_tit', fileTit);
        formData.append('file_dep', fileDep || "");

        try {
            const response = await fetch('/api/automacao-rh/rh-dirf-esocial', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; 
                link.setAttribute('download', 'Relatorio_Final_DIRF_eSocial.xlsx');
                document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
                
                setProgress(100); setStatusText('Relatório Transferido!');
                setMensagem({ type: 'success', text: 'Análise concluída com sucesso!' });
                
                setFileTotal(null); setFileTit(null); setFileDep(null);
                document.getElementById('form-dirf-analise').reset();
            } else {
                const data = await response.json();
                setMensagem({ type: 'error', text: data.error || 'Erro na análise.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de ligação ao servidor.' });
        } finally {
            setCarregandoAnalise(false); setTimeout(() => { setIsPolling(false); setProgress(0); }, 3000);
        }
    };

    
    const corModulo = '#e11d48'; // Vermelho RH
    const corFundoClaro = '#fff1f2';

    return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
        {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

        {/* Container Principal da Página */}
        <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* CABEÇALHO PREMIUM NO TOPO DA PÁGINA */}
            {!embedded && (
            <div style={{
                height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                alignItems: 'center', justifyContent: 'space-between',
                padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
            }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                            <ArrowLeft size={14} /> Voltar
                        </button>
                        <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> RH
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <ShieldCheck size={24} color={corModulo} /> Gestão da DIRF
                        </h2>
                        <button 
                            type="button"
                            onClick={() => setIsHelpOpen(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#fff1f2', color: corModulo, border: '1px solid #fecdd3', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                        >
                            <HelpCircle size={14} /> Como Utilizar
                        </button>
                    </div>
                </div>
                
                {!isMobile && (
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                        Olá, {nomeUsuario} 👋
                    </span>
                )}
            </div>
            )}

            {/* ÁREA DE CONTEÚDO */}
            <div style={{ padding: '2rem', flex: 1 }}>
                
                {/* MENSAGEM GLOBAL (Mostra sucesso/erro de qualquer uma das ações) */}
                {mensagem && (
                    <div className="max-w-4xl mx-auto mb-6">
                        <div style={{ padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', color: mensagem.type === 'success' ? '#065f46' : '#991b1b', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`}}>
                            {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>{mensagem.text}</span>
                        </div>
                    </div>
                )}

                {/* ========================================== */}
                {/* BLOCO 1: ANÁLISE DIRF VS ESOCIAL         */}
                {/* ========================================== */}
                <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-sm border border-gray-100 mb-8 relative">
                    
                    {/* Overlay de Bloqueio enquanto o outro processo roda */}
                    {carregandoDisparo && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, borderRadius: '12px' }}></div>}

                    <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileSpreadsheet size={20} color={corModulo} /> 1. Análise DIRF vs eSocial
                    </h3>
                    
                    <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                        <p style={{ color: '#9f1239', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                            Faça o upload das 3 planilhas bases extraídas do sistema para consolidar e validar as diferenças de valores antes de enviar aos colaboradores.
                        </p>
                    </div>

                    <form id="form-dirf-analise" onSubmit={handleAnaliseSubmit}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>Base TOTAL</label>
                                <div style={{ border: `2px dashed #fecdd3`, borderRadius: '8px', padding: '16px', textAlign: 'center', backgroundColor: '#fff' }}>
                                    <input type="file" accept=".xlsx, .xls" onChange={(e) => setFileTotal(e.target.files[0])} style={{ width: '100%', fontSize: '12px', color: '#475569' }} required />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>Base TITULARES</label>
                                <div style={{ border: `2px dashed #fecdd3`, borderRadius: '8px', padding: '16px', textAlign: 'center', backgroundColor: '#fff' }}>
                                    <input type="file" accept=".xlsx, .xls" onChange={(e) => setFileTit(e.target.files[0])} style={{ width: '100%', fontSize: '12px', color: '#475569' }} required />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>Base DEPENDENTES (Opcional)</label>
                                <div style={{ border: `2px dashed #fecdd3`, borderRadius: '8px', padding: '16px', textAlign: 'center', backgroundColor: '#fff' }}>
                                    <input type="file" accept=".xlsx, .xls" onChange={(e) => setFileDep(e.target.files[0])} style={{ width: '100%', fontSize: '12px', color: '#475569' }} />
                                </div>
                            </div>
                        </div>

                        {carregandoAnalise && (
                            <div style={{ marginBottom: '24px', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Timer size={16} className="animate-spin" color={corModulo} /> {statusText}
                                    </span>
                                    <span style={{ fontSize: '15px', fontWeight: '700', color: corModulo }}>{progress}%</span>
                                </div>
                                <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '999px', height: '10px', overflow: 'hidden' }}>
                                    <div style={{ width: `${progress}%`, backgroundColor: corModulo, height: '100%', transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}></div>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                            <button type="submit" disabled={carregandoAnalise || carregandoDisparo || !fileTotal || !fileTit} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '8px', backgroundColor: (carregandoAnalise || carregandoDisparo || !fileTotal || !fileTit) ? '#fecdd3' : corModulo, color: (carregandoAnalise || carregandoDisparo || !fileTotal || !fileTit) ? '#f43f5e' : '#fff', fontWeight: '600', border: 'none', cursor: (carregandoAnalise || carregandoDisparo || !fileTotal || !fileTit) ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                                {carregandoAnalise ? <><Timer size={18} className="animate-spin" /> Analisando...</> : <><Download size={18} /> Iniciar Cruzamento</>}
                            </button>
                        </div>
                    </form>
                </div>

                {/* ========================================== */}
                {/* BLOCO 2: DISPARO DE E-MAILS DA DIRF      */}
                {/* ========================================== */}
                <div className="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-sm border border-gray-100 relative">
                    
                    {/* Overlay de Bloqueio enquanto o outro processo roda */}
                    {carregandoAnalise && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, borderRadius: '12px' }}></div>}

                    <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Mail size={20} color={corModulo} /> 2. Disparo de Informe de Rendimentos (E-mail)
                    </h3>
                    
                    <div style={{ backgroundColor: '#f8fafc', borderLeft: `4px solid #f63b3b`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                        <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                            <strong>Instruções:</strong> Compacte todas as pastas contendo os PDFs da DIRF em um único arquivo <strong>.zip</strong>. 
                            O sistema irá descompactar, identificar o CPF no nome do arquivo, cruzar com a base de dados e enviar um e-mail com os anexos para cada colaborador.
                        </p>
                    </div>

                    <form id="form-dirf-disparo" onSubmit={handleDisparoSubmit}>
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                <FileArchive size={16} color="#f63b3b"/> Arquivo ZIP com os PDFs
                            </label>
                            <div style={{ border: `2px dashed #febfbf`, borderRadius: '8px', padding: '24px', textAlign: 'center', backgroundColor: '#ffefef' }}>
                                <input type="file" accept=".zip" onChange={(e) => setFileZip(e.target.files[0])} style={{ width: '100%', fontSize: '14px', color: '#475569', cursor: 'pointer' }} required />
                            </div>
                        </div>

                        {carregandoDisparo && (
                            <div style={{ marginBottom: '24px', padding: '20px', borderRadius: '12px', border: '1px solid #f0e2e2', backgroundColor: '#fcf8f8' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Timer size={16} className="animate-spin" color="#f63b3b" /> {statusText}
                                    </span>
                                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#f63b3b' }}>{progress}%</span>
                                </div>
                                <div style={{ width: '100%', backgroundColor: '#f0e2e2', borderRadius: '999px', height: '10px', overflow: 'hidden' }}>
                                    <div style={{ width: `${progress}%`, backgroundColor: '#f63b3b', height: '100%', transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}></div>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                            <button type="submit" disabled={carregandoDisparo || carregandoAnalise || !fileZip} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '8px', backgroundColor: (carregandoDisparo || carregandoAnalise || !fileZip) ? '#febfbf' : '#f63b3b', color: (carregandoDisparo || carregandoAnalise || !fileZip) ? '#fa6060' : '#fff', fontWeight: '600', border: 'none', cursor: (carregandoDisparo || carregandoAnalise || !fileZip) ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                                {carregandoDisparo ? <><Timer size={18} className="animate-spin" /> Processando Disparos...</> : <><Send size={18} /> Iniciar Disparos</>}
                            </button>
                        </div>
                    </form>
                </div>

            </div>
        </div>
        
        {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA DIRF vs ESOCIAL */}
        <HelpCenter 
            isOpen={isHelpOpen} 
            onClose={() => setIsHelpOpen(false)} 
            secaoInicial="rh-dirf-esocial" 
            showOnlyIds={['rh-dirf-esocial']} 
        />
    </div>
    );
};

export default AutomacaoDirfEsocial;